import Controller from '@ember/controller';
import { service } from '@ember/service';

import { getEnvironmentVariables } from '@repo/shared-config/app/helper/environment-variables';
import { formatMoney } from '@repo/design-system-kit/utils/format-money';

import { MainLayout } from 'register-partners-js/react/components/layouts/main';
import {
  getDougsDisplayName,
  isDougsCobrandingEnabled,
} from 'register-partners-js/utils/is-cobranding-dougs';
import { RegisterPartnersStripeCardForm } from 'register-partners-js/react/components/stripe-card-form/register-partners-stripe-card-form';

import styles from './styles.module.css';

export default class RegistrationPaymentController extends Controller {
  mainLayout = MainLayout;
  stripeCardForm = RegisterPartnersStripeCardForm;

  @service router;
  @service segment;
  @service currentUser;
  @service intl;

  get stripePublishableKey() {
    return getEnvironmentVariables().stripe.publishableKey;
  }

  get registrationId() {
    return this.registration.id;
  }

  get resolvedPricePlanId() {
    return this.model.resolvedPricePlan.id;
  }

  get selectedPricePlanRecurrence() {
    return this.model.selectedPricePlan.recurrence;
  }

  get capitalizedBrandName() {
    return this.model.estimation.product.brand_name;
  }

  get promoOffer() {
    return this.model.estimation.product.promo_discount;
  }

  get paymentInvoiceAmount() {
    return this.model.estimation.total_amount.value;
  }

  get regularPrice() {
    return this.model.estimation.product.price_amount.value;
  }

  get stripeCardFormPromoOffer() {
    if (!this.promoOffer) return undefined;

    return {
      monthDuration: this.promoOffer.month_duration,
      discountedPrice: this.promoOffer.discounted_price.value,
    };
  }

  get displayAmount() {
    return formatMoney({
      amount: this.model.estimation.total_vat_included_amount.value,
      token: 'money-symbol-compact',
    });
  }

  get paymentSubtitle() {
    if (isDougsCobrandingEnabled(this.model.registration.partnerName)) {
      return this.intl.t('order.payment.partner.subtitle', {
        partner: getDougsDisplayName(this.model.registration.partnerName),
      });
    }
    return this.intl.t('order.payment.subtitle');
  }

  handlePaymentSuccess = () => {
    let organizationId = this.registration.belongsTo('organization').id();
    let selectedPricePlan = this.model.resolvedPricePlan;
    let recurrence = this.selectedPricePlanRecurrence;
    let { tracking_details } = selectedPricePlan;

    let revenue =
      recurrence === 'annual'
        ? tracking_details.annual_plan_conversion_value
        : tracking_details.monthly_plan_conversion_value;

    let yearlyRevenue =
      recurrence === 'annual'
        ? tracking_details.annual_plan_yearly_revenue
        : tracking_details.monthly_plan_yearly_revenue;

    this.segment.track('onboarding_partner_payment_confirmed');

    this.segment.track('onboarding_partner_contract_signed', {
      original_price_plan_code: this.model.selectedPricePlan.product_id,
      recurrence,
      revenue,
      k_deposit: 'true',
      ec_order_id: this.currentUser.user?.segmentId,
      ec_order_revenue: yearlyRevenue,
      currency: tracking_details.conversion_currency,
      ec_product: {
        product_id: selectedPricePlan.id,
        product_name: selectedPricePlan.code,
        product_brand: 'Qonto',
        product_category: 'Incorporated',
        product_category2: selectedPricePlan.lineup,
        product_price: yearlyRevenue,
        product_quantity: 1,
      },
      organization_id: organizationId,
      order_id: organizationId,
    });

    this.router.transitionTo('registration.almost-ready');
  };

  handlePaymentError = () => {
    this.segment.track('onboarding_partner_payment_failed');
    this.router.transitionTo('registration.payment-error');
  };

  styles = styles;
}
